import type { FC } from "react";
import { Radio } from "@privacy/ui";
import MuiRadioGroup from "@mui/material/RadioGroup";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { useController, useFormContext } from "react-hook-form";
import { yesOrNoInfo } from "../../inlineForm/inputs/YesOrNo";
import type { YesOrNoWithDescriptionProps, YesOrNoValue } from "../../inlineForm/inputs/YesOrNo";
import { ErrorMessage } from "../../inlineForm/ErrorMessage";
import { isAnswerRequestEmpty } from "../../shared/isAnswerRequestEmpty";
import { useDynamicFormState } from "../../DynamicFormProvider";
import { Option } from "./Option";

export const YesOrNoRadioGroup: FC<YesOrNoWithDescriptionProps> = ({
  questionCard,
  readonly,
  onSave,
  yesDescription,
  noDescription,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { field } = useController({ control, name: questionCard.id });
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();

  const saveMutation = useMutation({
    mutationFn: (value: YesOrNoValue) => {
      field.onChange(value);
      return onSave(value);
    },
  });

  return (
    <>
      <MuiRadioGroup value={field.value}>
        <Option
          disabled={readonly}
          checked={field.value === "yes"}
          onClick={() => saveMutation.mutate(field.value === "yes" ? null : "yes")}
          option={{ value: "yes", label: t(yesOrNoInfo.yes.labelKey), description: yesDescription }}
          Control={Radio}
        />
        <Option
          disabled={readonly}
          checked={field.value === "no"}
          onClick={() => saveMutation.mutate(field.value === "no" ? null : "no")}
          option={{ value: "no", label: t(yesOrNoInfo.no.labelKey), description: noDescription }}
          Control={Radio}
        />
      </MuiRadioGroup>
      {!saveMutation.isPending && saveMutation.isError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </>
  );
};
